import * as THREE from 'three';
import { Track, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';
import { glowSprite, cloudSprite, facadeTexture } from '../proctex.js';
import { sampleRibbon, clearOfRibbon } from './placement.js';

/**
 * THE ASCENT — launch-complex haze to hard vacuum in one climb. The sky, fog and
 * stars crossfade with camera altitude: gantries and a cloud deck below, black
 * space and the glowing curve of the atmosphere above.
 */
export function buildTheAscent(scene: THREE.Scene, track: Track, quality: GraphicsPreset): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng('the-ascent');

  const ribbon = sampleRibbon(track, 80);
  const { cx, cz, minY, maxY } = ribbon;
  const groundY = minY - 45;

  // altitude band where atmosphere gives way to space; adapts to flat tracks so
  // the crossfade always lives inside the climb
  const span = Math.max(60, maxY - minY);
  const fadeLo = minY + Math.min(150, span * 0.35);
  const fadeHi = fadeLo + Math.max(120, span * 0.45);

  // ── sky: altitude-blended atmosphere → space, with a limb glow at the horizon ──
  const skyMat = (() => {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: { uSpace: { value: 0 } },
      vertexShader: `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec3 vDir;
        uniform float uSpace;
        void main(){
          float h = vDir.y * 0.5 + 0.5;
          // launch-complex morning haze
          vec3 atmoZenith = vec3(0.055, 0.115, 0.220);
          vec3 atmoHorizon = vec3(0.360, 0.430, 0.520);
          vec3 atmo = mix(atmoHorizon, atmoZenith, pow(h, 0.85));
          // hard vacuum
          vec3 space = mix(vec3(0.006, 0.010, 0.022), vec3(0.001, 0.001, 0.004), pow(h, 0.7));
          vec3 col = mix(atmo, space, uSpace);
          // the planet's limb: a thin curved glow just below eye level that
          // sharpens and brightens as you leave the atmosphere
          float limb = exp(-abs(vDir.y + 0.045) * (14.0 + uSpace * 26.0));
          col += vec3(0.25, 0.55, 0.95) * limb * (0.15 + uSpace * 1.15);
          col += vec3(0.9, 0.65, 0.35) * exp(-abs(vDir.y + 0.02) * 60.0) * uSpace * 0.5;
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
    return mat;
  })();

  // ── stars: fade in above the atmosphere ──
  const starMat = (() => {
    const count = quality === 'performance' ? 1000 : 2400;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const a = rng.next() * Math.PI * 2;
      const z = rng.range(-0.1, 1);
      const r = Math.sqrt(Math.max(0, 1 - z * z));
      pos[i * 3] = Math.cos(a) * r * 3500;
      pos[i * 3 + 1] = z * 3500;
      pos[i * 3 + 2] = Math.sin(a) * r * 3500;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2.2,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const stars = new THREE.Points(geo, mat);
    scene.add(stars);
    objects.push(stars);
    disposables.push(geo, mat);
    return mat;
  })();

  // ── lights: high morning sun, cool bounce ──
  {
    const hemi = new THREE.HemisphereLight(0x6a8ab0, 0x141a22, 0.8);
    const sun = new THREE.DirectionalLight(0xfff0d8, 1.4);
    sun.position.set(cx + 600, minY + 1100, cz - 300);
    const fill = new THREE.DirectionalLight(0x31c8ff, 0.15);
    fill.position.set(cx - 500, minY + 100, cz + 600);
    scene.add(hemi, sun, fill);
    objects.push(hemi, sun, fill);
  }

  // ── launch complex: pad floor + lit gantry towers around the low section ──
  {
    const geo = new THREE.PlaneGeometry(8000, 8000);
    const mat = new THREE.MeshStandardMaterial({ color: 0x11161e, roughness: 0.85, metalness: 0.3 });
    const floor = new THREE.Mesh(geo, mat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(cx, groundY, cz);
    scene.add(floor);
    objects.push(floor);
    disposables.push(geo, mat);
  }
  {
    const count = quality === 'performance' ? 60 : 130;
    const tex = facadeTexture(29);
    disposables.push(tex);
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.5,
      color: 0x1a2028,
      roughness: 0.75,
      metalness: 0.45,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const dummy = new THREE.Object3D();
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const x = cx + rng.range(-1600, 1600);
      const z = cz + rng.range(-1600, 1600);
      if (!clearOfRibbon(ribbon, x, z, 95)) continue;
      if (rng.chance(0.7)) {
        // service tower
        const h = rng.range(60, 240);
        dummy.position.set(x, groundY + h / 2, z);
        dummy.scale.set(rng.range(10, 26), h, rng.range(10, 26));
      } else {
        // low assembly hall
        const h = rng.range(18, 44);
        dummy.position.set(x, groundY + h / 2, z);
        dummy.scale.set(rng.range(60, 150), h, rng.range(40, 100));
      }
      dummy.rotation.y = rng.chance(0.5) ? 0 : Math.PI / 2;
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo, mat);
  }

  // ── cloud deck at the crossfade altitude: racing up punches through it ──
  {
    const count = quality === 'performance' ? 40 : 90;
    const tex = cloudSprite(7);
    disposables.push(tex);
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      color: 0xc2ccda,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const dummy = new THREE.Object3D();
    const cloudY = fadeLo + 10;
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const x = cx + rng.range(-2200, 2200);
      const z = cz + rng.range(-2200, 2200);
      const y = cloudY + rng.range(-18, 18);
      // keep clouds off any ribbon section passing through the deck altitude
      let blocked = false;
      for (const p of ribbon.ring) {
        if (Math.abs(p.y - y) < 90 && Math.hypot(p.x - x, p.z - z) < 260) {
          blocked = true;
          break;
        }
      }
      if (blocked) continue;
      dummy.position.set(x, y, z);
      dummy.rotation.set(-Math.PI / 2, 0, rng.range(0, Math.PI * 2));
      const s = rng.range(180, 460);
      dummy.scale.set(s, s, s);
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo, mat);
  }

  // ── atmosphere glow seen from above: a vast soft disc over the ground haze ──
  {
    const tex = glowSprite();
    disposables.push(tex);
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0x4a7ab0,
      fog: false,
    });
    const glow = new THREE.Mesh(geo, mat);
    glow.rotation.x = -Math.PI / 2;
    glow.position.set(cx, groundY + 4, cz);
    glow.scale.setScalar(9000);
    scene.add(glow);
    objects.push(glow);
    disposables.push(geo, mat);
  }

  // ── fog crossfade state: reuse the renderer's fog, remember its ground values ──
  const fog = scene.fog instanceof THREE.FogExp2 ? scene.fog : null;
  const baseDensity = fog?.density ?? 0.0016;
  const hazeColor = new THREE.Color(fog ? fog.color.getHex() : 0x27384e);
  const spaceColor = new THREE.Color(0x02040a);

  return {
    update(_dt, camPos, _time) {
      // altitude factor: 0 = launch pad, 1 = vacuum
      const f = THREE.MathUtils.smoothstep(camPos.y, fadeLo, fadeHi);
      skyMat.uniforms.uSpace.value = f;
      starMat.opacity = f * 0.95;
      if (fog) {
        fog.density = baseDensity * (1 - f * 0.96);
        fog.color.lerpColors(hazeColor, spaceColor, f);
      }
    },
    dispose() {
      // put the shared fog back the way the renderer set it
      if (fog) {
        fog.density = baseDensity;
        fog.color.copy(hazeColor);
      }
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
